import {ContextMenu} from "@/shared/ui/contextMenu";

/**
 * Closes all timeline context menus within the page content.
 * Includes context menu opened via right-click (.context-table-row) and via clicking a button (.dropdown)
 *
 * @return {void} Does not return a value.
 */
export function closeOpenTimelineContextMenus(): void {
	$(document).find(".context-table-row.open, [data-context-menu-container].open").removeClass("open");
	$(document).find("[data-toggle='context'].context-menu-open").removeClass("context-menu-open");
}

/**
 * Context menu for use in timeline web components (which avoid jQuery)
 * This class is configured to invoke the context menu without jQuery selectors
 */
export class TimelineContextMenu<T> extends ContextMenu<T> {

	private static readonly SIDEBAR_RIGHT_ANCHOR_THRESHOLD_PX = 5;
	private static readonly HOST_EVENT_NAMESPACE = ".timelineContextMenuHost";
	private static readonly RIGHT_CLICK_EVENT_NAMESPACE = "contextmenu.timelineContextMenu";

	public static readonly HOST_ID = "timeline-context-menu-host";
	public static readonly HOST_SELECTOR = `#${TimelineContextMenu.HOST_ID}`;

	constructor() {
		super(true);
	}

	/**
	 * Binds the context menu to a specified element.
	 *
	 * @param element The element to which the context menu should be bound.
	 * @param recording Optional recording data to be used in the context menu.
	 */
	public bindToElement(element: Element, recording?: T): void {
		const containerEl: HTMLElement = element.querySelector("[data-context-menu-container]");
		const buttonEl: HTMLElement = element.querySelector("[data-context-menu-button]");

		if (containerEl && buttonEl) {
			containerEl.append(this.render(recording));
			this.registerOnElement(buttonEl, recording);
		}
	}

	/**
	 * Renders the context menu for the given definition (which opens to the left by default)
	 */
	private render(recording?: T) {
		// Render right-anchored by default so menu opens to the left first - alignDropdownMenuToVisibleArea() then flips to left-anchored only when needed
		// DEVNOTE: Some context menus can override this via CSS where automatic logic is not desired e.g., .panel-none .context-menu {left: 0}
		const dropdown = this.renderDropdown(recording, true);
		return dropdown !== "" ? dropdown.get(0) : "";
	}

	/**
	 * Registers event listeners on the given button element to handle context menu interactions.
	 *
	 * @param element The button element to which the event listeners will be attached.
	 * @param recording Optional recording data to be used in the context menu.
	 * @param allowRightClick Whether to allow right-click functionality for the context menu (default: true).
	 */
	private registerOnElement(element: HTMLElement, recording?: T, allowRightClick: boolean = true) {
		const $button = $(element);
		const $dropdown = $button.closest(".dropdown");
		const $contextItem = $button.closest("[data-toggle='context']");

		$button.on("click", (event) => {
			const element = $(event.currentTarget);
			// remove old dropdown menu
			element.siblings("ul.dropdown-menu").remove();
			// render new dropdown menu with updated show conditions
			element.parent().append(this.render(recording));
		});

		// Toggle context menu on dropdown click
		$dropdown
			.on("click", e => {
				const isOpen = $dropdown.hasClass("open");
				closeOpenTimelineContextMenus();

				$dropdown.toggleClass("open", !isOpen);
				$contextItem.toggleClass("context-menu-open", !isOpen);

				// align dropdown menu to visible area on opening, to ensure it is not obscured by the sidebar
				if (!isOpen) {
					this.alignDropdownMenuToVisibleArea($dropdown);
				}
				e.stopPropagation();
			})
			.on("click", "ul.dropdown-menu > li", e => this.onContextMenuItemClick(e, recording, element));

		if (allowRightClick) {
			$contextItem.attr("data-target", TimelineContextMenu.HOST_SELECTOR);
			this.onContextMenuClick($contextItem, element, recording);
		}
	}

	private onContextMenuClick(timelineItem: JQuery, button: HTMLElement, recording?: T): void {
		timelineItem
			.off(TimelineContextMenu.RIGHT_CLICK_EVENT_NAMESPACE)
			.on(TimelineContextMenu.RIGHT_CLICK_EVENT_NAMESPACE, (event) => {
				// make sure that the event actually belongs to the correct timeline item
				if (event.target.closest("[data-toggle='context']") !== timelineItem.get(0)) {
					return;
				}

				closeOpenTimelineContextMenus();

				if (!!event.target.closest(".subitem-gap, .subitem-overlap") || !this.prepareRightClickHost(recording, button)) {
					event.preventDefault();
					event.stopImmediatePropagation();
				}
			});
	}

	private onContextMenuItemClick(e: JQuery.TriggeredEvent, recording: T, button: HTMLElement) {
		const element = $(e.currentTarget);
		if (!element.hasClass("disabled")) {
			const action = element.data("action") as string;
			this.triggerAction(action, recording, $(button));
		}
	}

	private prepareRightClickHost(recording: T | undefined, button: HTMLElement): boolean {
		const contextMenuHost = $(TimelineContextMenu.HOST_SELECTOR);
		if (contextMenuHost.length === 0) {
			console.error(`Missing shared timeline context menu host: ${TimelineContextMenu.HOST_SELECTOR}`);
			return false;
		}

		const contextMenu = this.renderDropdown(recording);
		if (contextMenu === "") {
			return false;
		}

		contextMenuHost
			.empty()
			.append(contextMenu)
			.off(`hidden.bs.context${TimelineContextMenu.HOST_EVENT_NAMESPACE}`)
			.on(`hidden.bs.context${TimelineContextMenu.HOST_EVENT_NAMESPACE}`, () => {
				contextMenuHost
					.empty()
					.off(`click${TimelineContextMenu.HOST_EVENT_NAMESPACE}`, "ul.dropdown-menu > li");
			})
			.off(`click${TimelineContextMenu.HOST_EVENT_NAMESPACE}`, "ul.dropdown-menu > li")
			.on(`click${TimelineContextMenu.HOST_EVENT_NAMESPACE}`, "ul.dropdown-menu > li", event => this.onContextMenuItemClick(event, recording, button));
		return true;
	}

	/**
	 * Aligns the dropdown menu to ensure it is fully visible within the viewport, especially in relation to the sidebar.
	 * The method checks the position of the dropdown menu and adjusts its alignment to prevent it from being obscured by the sidebar.
	 * @param dropdown The jQuery dropdown object representing element whose menu needs to be aligned with an anchor class
	 */
	private alignDropdownMenuToVisibleArea(dropdown: JQuery): void {
		const $menu = dropdown.children("ul.dropdown-menu");
		if ($menu.length === 0) {
			return;
		}

		// calculate the right boundary of the sidebar and the left position of the dropdown menu
		const sidebarBoundaryRightPx = document.querySelector(".sidebar").getBoundingClientRect().right + TimelineContextMenu.SIDEBAR_RIGHT_ANCHOR_THRESHOLD_PX;

		// get the left position of the dropdown menu
		const dropdownMenuLeftPositionPx = $menu.get(0).getBoundingClientRect().left;

		// default context menu anchor is right (meaning context menu opens to the left)
		// depending on the available space on the left side of the dropdown menu decide whether to set to left or right (default)
		$menu.removeClass("dropdown-menu-right dropdown-menu-left");
		if (dropdownMenuLeftPositionPx < sidebarBoundaryRightPx) {
			// not enough space to the sidebar -> anchor is left (flipped)
			$menu.addClass("dropdown-menu-left");
		} else {
			// enough space to the sidebar -> anchor is right (default)
			$menu.addClass("dropdown-menu-right");
		}
	}
}
